import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface AddressSuggestion {
  description?: string;
  place_id?: string;
  structured_formatting?: {
    main_text?: string;
    secondary_text?: string;
    main_text_matched_substrings?: Array<{ offset: number; length: number }>;
  };
  [key: string]: any;
}

function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildHighlightRegex(query: string) {
  const parts = query.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return null;
  return new RegExp(`(${parts.map(escapeRegex).join('|')})`, 'ig');
}

function HighlightedText({ text, query }: { text: string; query: string }) {
  // CB:BEGIN remove-highlighting
  // Return plain text without any highlighting
  return <>{text}</>;
  // CB:END remove-highlighting
}

export function useAddressAutocomplete(apiBaseUrl: string) {
  const [address, setAddress] = useState('');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const fetchSuggestions = useCallback(
    async (input: string) => {
      const trimmed = input.trim();
      if (trimmed.length < 3) {
        setSuggestions([]);
        setShowSuggestions(false);
        setActiveIndex(-1);
        return;
      }

      setIsLoadingSuggestions(true);
      try {
        const res = await fetch(
          `${apiBaseUrl}/api/autocomplete?input=${encodeURIComponent(trimmed)}`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const preds = (data && data.predictions) || [];
        const limited = preds.slice(0, 3);
        setSuggestions(limited);
        setShowSuggestions(limited.length > 0);
        setActiveIndex(-1);
      } catch (err) {
        console.warn('Autocomplete failed (non-fatal):', err);
        setSuggestions([]);
        setShowSuggestions(false);
        setActiveIndex(-1);
      } finally {
        setIsLoadingSuggestions(false);
      }
    },
    [apiBaseUrl]
  );

  const handleAddressChange = useCallback(
    (value: string) => {
      setAddress(value);
      fetchSuggestions(value);
    },
    [fetchSuggestions]
  );

  const handleSuggestionSelect = useCallback((suggestion: AddressSuggestion) => {
    const label =
      suggestion?.description ||
      suggestion?.structured_formatting?.main_text ||
      '';
    setAddress(label);
    setShowSuggestions(false);
    setSuggestions([]);
    setActiveIndex(-1);
  }, []);

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setShowSuggestions(false);
    setActiveIndex(-1);
  }, []);

  const moveSelection = useCallback(
    (direction: 'up' | 'down') => {
      setActiveIndex((prev) => {
        if (!suggestions.length) return -1;
        if (direction === 'down') return prev < suggestions.length - 1 ? prev + 1 : 0;
        return prev > 0 ? prev - 1 : suggestions.length - 1;
      });
    },
    [suggestions]
  );

  const selectActive = useCallback(() => {
    if (activeIndex >= 0 && suggestions[activeIndex]) {
      handleSuggestionSelect(suggestions[activeIndex]);
    }
  }, [activeIndex, suggestions, handleSuggestionSelect]);

  return {
    address,
    setAddress,
    suggestions,
    showSuggestions,
    isLoadingSuggestions,
    handleAddressChange,
    handleSuggestionSelect,
    clearSuggestions,
    activeIndex,
    setActiveIndex,
    moveSelection,
    selectActive,
  };
}

interface SuggestionListProps {
  suggestions: AddressSuggestion[];
  visible: boolean;
  onSelect: (suggestion: AddressSuggestion) => void;
  query: string;
  activeIndex: number;
  setActiveIndex: (idx: number) => void;
}

export const SuggestionList: React.FC<SuggestionListProps> = ({
  suggestions,
  visible,
  onSelect,
  query,
  activeIndex,
  setActiveIndex,
}) => {
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    if (activeIndex < 0) return;
    const el = itemRefs.current[activeIndex];
    if (!el) return;
    el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!visible || !suggestions.length) return null;

  return (
    <div className="w-full rounded border border-slate-200/70 bg-white shadow-xl overflow-hidden">
      <div className="max-h-[220px] overflow-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        {suggestions.map((sug, idx) => {
          const active = idx === activeIndex;

          const main =
            sug.description ||
            sug.structured_formatting?.main_text ||
            'Select address';

          return (
            <button
              ref={(node) => {
                itemRefs.current[idx] = node;
              }}
              type="button"
              key={`${sug.place_id || sug.description || idx}`}
              className={[
                'w-full px-4 py-3',
                'flex items-center justify-center text-center',
                'text-[15px] leading-snug font-medium',
                'border-b border-slate-200/60 last:border-b-0',
                'transition-colors duration-75 truncate',
                active ? 'bg-slate-50' : 'hover:bg-slate-50',
                'focus:outline-none',
              ].join(' ')}
              onMouseEnter={() => setActiveIndex(idx)}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(sug);
              }}
            >
              <div className="truncate text-slate-900">
                <HighlightedText text={main} query={query} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
